import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Search, X } from 'lucide-react';
import { useLocation } from 'wouter';

import { apiGet } from '../../lib/api_v2';
import { buildMessageEntitySnapshots } from '../../lib/entitySnapshots';
import { logError } from '../../lib/logger';
import { getMetaContent } from '../../lib/meta';
import { requestMessageJump } from '../../lib/pendingMessageJump';
import { cn } from '../../lib/utils';
import { searchResultRoutePath } from '../../lib/workspaceRoutes';

import { EntitySnapshotContext } from '../../contexts/EntitySnapshotContext';
import { useRouteWorkspace } from '../../contexts/RouteWorkspaceContext';
import LocalTime from '../messages/LocalTime';
import { MESSAGE_CONTENT_TYPOGRAPHY_CLASS } from '../messages/Message';
import { getMessageBubbleSurfaceClassName } from '../messages/messageStyleClasses';
import RichdownContent from '../messages/RichdownContent';
import Flexicon from '../shared/Flexicon';

// Mirrored from Api::V2::SearchController::MAX_QUERY_LENGTH via the application layout meta tag,
// so the client and server enforce the same cap without either side hardcoding the number.
const MAX_QUERY_LENGTH = parseInt(getMetaContent('search:max_query_length'), 10) || 200;
const SEARCH_RESULTS_PER_PAGE = 200;

// Lets a row's click handler defer to inner interactive descendants (links, mention/entity
// pills, embedded buttons) instead of stealing the click for navigation.
function shouldIgnoreRowOpenEvent(event) {
  const target = event.target;
  if (!(target instanceof Element)) return false;

  const nestedInteractive = target.closest('a, button, input, select, textarea, [role="button"], entity');
  return nestedInteractive && nestedInteractive !== event.currentTarget;
}

// Same hairline recipe as SettingsList: dividers fade out beside the hovered row so its
// hover fill gets clean edges. Precomputed per surface so the results map doesn't run
// cn() once per divider.
const RESULT_DIVIDER_BASE_CLASS =
  'h-[0.5px] w-[calc(100%-40px)] shrink-0 self-center transition-opacity duration-300 ease-fluid transform-gpu will-change-[opacity] [:hover+&]:opacity-0 [&:has(+:hover)]:opacity-0';
const RESULT_DIVIDER_CLASS = {
  onImage: `${RESULT_DIVIDER_BASE_CLASS} bg-white/20`,
  plain: `${RESULT_DIVIDER_BASE_CLASS} bg-gray-200 dark:bg-neutral-800`,
};

// Memoized: a keystroke in the input re-renders the whole list otherwise, re-parsing
// every result's HTML (up to SEARCH_RESULTS_PER_PAGE rows). All props are stable
// across query-state changes until a new search actually lands.
const SearchResultRow = React.memo(function SearchResultRow({ message, author, routeEntitySnapshots, onOpen, onImage, highlightTerm }) {
  const entitySnapshots = useMemo(
    () => buildMessageEntitySnapshots(message.text, routeEntitySnapshots),
    [message.text, routeEntitySnapshots],
  );

  // Mirror handleOpenSearchResult's preconditions exactly so a row never advertises
  // itself as clickable (cursor + role="button") but then no-ops on click.
  const canOpen = Boolean(message.workspace_id && message.channel_id && message.sort_order);

  const handleClick = useCallback(
    function handleClick(event) {
      if (!canOpen) return;
      if (shouldIgnoreRowOpenEvent(event)) return;
      onOpen(message);
    },
    [canOpen, message, onOpen],
  );

  const handleKeyDown = useCallback(
    function handleKeyDown(event) {
      if (!canOpen) return;
      if (event.key !== 'Enter' && event.key !== ' ') return;
      if (shouldIgnoreRowOpenEvent(event)) return;
      event.preventDefault();
      onOpen(message);
    },
    [canOpen, message, onOpen],
  );

  return (
    <div
      role={canOpen ? 'button' : undefined}
      tabIndex={canOpen ? 0 : undefined}
      onClick={canOpen ? handleClick : undefined}
      onKeyDown={canOpen ? handleKeyDown : undefined}
      data-app-region="none"
      className={cn(
        'flex gap-3.5 px-5 py-4',
        canOpen && 'cursor-pointer text-left focus:outline-none',
        canOpen &&
          (onImage
            ? 'hover:bg-white/10 focus-visible:bg-white/10'
            : 'hover:bg-white focus-visible:bg-white dark:hover:bg-neutral-800 dark:focus-visible:bg-neutral-800'),
      )}
    >
      <span className="mt-0.5 inline-flex size-7 shrink-0 overflow-hidden rounded-full">
        {author ? (
          <Flexicon resource={author} size="size-7" rounded="rounded-full" className="size-7" />
        ) : (
          <span className={cn('size-7 rounded-full', onImage ? 'bg-white/20' : 'bg-gray-200 dark:bg-neutral-700')} />
        )}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex items-baseline gap-2">
          <span
            className={cn(
              'truncate font-sans text-[13px]/[20px] font-semibold',
              onImage ? 'text-white' : 'text-black dark:text-neutral-200',
            )}
          >
            {author?.full_name || 'Unknown'}
          </span>
          <LocalTime
            timestamp={message.timestamp}
            format="%-m/%-d/%y · %-I:%M %p"
            className={cn('shrink-0 font-sans text-[12px]/[20px]', onImage ? 'text-white/60' : 'text-gray-500 dark:text-neutral-500')}
          />
        </div>
        {/* The real received-bubble recipe (same as ActivityFeed), so results render
            exactly like chat messages in every theme and over background images. */}
        <div className={cn('richdown-bubble--base', getMessageBubbleSurfaceClassName(false), 'w-fit max-w-full')}>
          <RichdownContent
            html={message.text}
            entitySnapshots={entitySnapshots}
            richdownVariant={cn('richdown-content--channel-message-not-mine', MESSAGE_CONTENT_TYPOGRAPHY_CLASS)}
            containsOnlyEntities={message.contains_only_entities}
            containsOnlyEmojiEntities={message.contains_only_emoji_entities}
            highlightTerm={highlightTerm}
          />
        </div>
      </div>
    </div>
  );
});

// Inline workspace search: the input pill lives in the nav bar (this component renders
// in the nav's left grid cell) and results drop into a floating card anchored below the
// bar — the Channels-card recipe — so the chat stays visible behind them.
const HeaderSearch = ({ onImage = false, onRequestClose }) => {
  const { workspaceId, directMessageId } = useRouteWorkspace();
  const routeEntitySnapshots = useContext(EntitySnapshotContext);
  const [, navigate] = useLocation();
  const [query, setQuery] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [results, setResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const abortRef = useRef(null);
  const isMountedRef = useRef(true);
  const resultsScrollRef = useRef(null);
  const searchInputRef = useRef(null);
  const rootRef = useRef(null);

  // The pill expands via a max-width transition kicked off after the initial styles have
  // definitely been committed (double rAF). A mount keyframe animation glitched on the
  // first-ever open: the first frame painted at full width before the animation engaged.
  const [isPillExpanded, setIsPillExpanded] = useState(false);

  // Focus immediately so Cmd+F-then-type never drops keystrokes, but with preventScroll:
  // a plain focus() scrolls the still-collapsed overflow-hidden pill to reveal the caret,
  // which shoves the magnifier left for a beat before the expansion unwinds it.
  useEffect(function focusInputOnMount() {
    searchInputRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(function expandPillAfterMount() {
    let innerFrame = null;
    const outerFrame = requestAnimationFrame(() => {
      innerFrame = requestAnimationFrame(() => setIsPillExpanded(true));
    });
    return () => {
      cancelAnimationFrame(outerFrame);
      if (innerFrame !== null) cancelAnimationFrame(innerFrame);
    };
  }, []);

  // Clicking anywhere outside the pill or the results card collapses search,
  // mirroring the Esc key. pointerdown (not click) so a drag that starts outside
  // still dismisses, matching how popovers behave.
  useEffect(
    function closeOnOutsidePointerDown() {
      function handlePointerDown(event) {
        if (!(event.target instanceof Node)) return;
        if (rootRef.current?.contains(event.target)) return;
        onRequestClose?.();
      }

      document.addEventListener('pointerdown', handlePointerDown);
      return () => {
        document.removeEventListener('pointerdown', handlePointerDown);
      };
    },
    [onRequestClose],
  );

  useEffect(function trackMountedSearch() {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const trimmedQuery = query.trim();

  const authorIds = useMemo(
    () =>
      Array.from(
        new Set(
          results
            .filter((message) => message.author_type === 'profile' && message.workspace_id === workspaceId && message.author_id)
            .map((message) => message.author_id),
        ),
      ).sort(),
    [results, workspaceId],
  );
  // Authors share the same workspace-wide Map that entity-pill resolution uses, so the byline and
  // any mention inside the message can never disagree about a profile's name/flexicon.
  const authorsById = useMemo(() => {
    if (!routeEntitySnapshots?.profile) return {};
    const map = {};
    for (const authorId of authorIds) {
      const snapshot = routeEntitySnapshots.profile.get(authorId);
      if (snapshot) map[authorId] = snapshot;
    }
    return map;
  }, [authorIds, routeEntitySnapshots]);

  const handleQueryChange = useCallback(function handleQueryChange(event) {
    setQuery(event.target.value);
  }, []);

  const resetSearch = useCallback(function resetSearch() {
    setResults([]);
    setTotalResults(0);
    setCurrentPage(1);
    setSubmittedQuery('');
    setIsLoading(false);
  }, []);

  // Open a search result in its channel, landing centered on the specific message.
  //
  // We write the jump intent into the pendingMessageJump buffer first and then
  // navigate to the clean channel URL — no query params on the wire. The new
  // channel's MessageFeed drains the buffer during its channel switch (cross-channel
  // case) or its CustomEvent listener (same-channel case), so the user goes
  // straight to the centered message. Opening a result closes the search overlay.
  //
  // Search is scoped to the current route workspace, but direct messages use their
  // public conversation id instead of exposing the hidden workspace as a route.
  const handleOpenSearchResult = useCallback(
    function handleOpenSearchResult(message) {
      if (!message?.workspace_id || !message?.channel_id || !message?.sort_order) return;
      requestMessageJump({ channelId: message.channel_id, sortOrder: message.sort_order });
      navigate(
        `~${searchResultRoutePath({
          workspaceId: message.workspace_id,
          directMessageId,
          channelId: message.channel_id,
        })}`,
      );
      onRequestClose?.();
    },
    [directMessageId, navigate, onRequestClose],
  );

  const runSearch = useCallback(
    async function runSearch(searchTerm, page) {
      if (abortRef.current) abortRef.current.abort();

      if (!searchTerm || !workspaceId) {
        resetSearch();
        abortRef.current = null;
        return;
      }

      const controller = new AbortController();
      abortRef.current = controller;

      setIsLoading(true);
      setCurrentPage(page);

      try {
        const searchParams = new URLSearchParams({
          workspace_id: workspaceId,
          q: searchTerm,
          page: String(page),
          per_page: String(SEARCH_RESULTS_PER_PAGE),
        });
        const response = await apiGet(`/search?${searchParams.toString()}`, { signal: controller.signal });
        const data = await response.json();

        if (controller.signal.aborted) return;

        if (data?.ok) {
          const responseData = data.data || {};
          setResults(responseData.messages || []);
          setTotalResults(responseData.total_messages || 0);
          setCurrentPage(responseData.page || page);
          resultsScrollRef.current?.scrollTo({ top: 0 });
        } else {
          logError('search', `Failed to search: ${JSON.stringify(data?.errors)}`);
          setResults([]);
          setTotalResults(0);
        }
      } catch (error) {
        if (error?.name === 'AbortError') return;
        logError('search', 'Failed to search', error);
        setResults([]);
        setTotalResults(0);
      } finally {
        if (isMountedRef.current && abortRef.current === controller) {
          abortRef.current = null;
          setIsLoading(false);
        }
      }
    },
    [resetSearch, workspaceId],
  );

  const handleSubmit = useCallback(
    async function handleSubmit(event) {
      event.preventDefault();
      if (!trimmedQuery || trimmedQuery.length > MAX_QUERY_LENGTH || isLoading) return;

      setSubmittedQuery(trimmedQuery);
      await runSearch(trimmedQuery, 1);
    },
    [isLoading, runSearch, trimmedQuery],
  );

  // The clear chip empties the field first; on an already-empty field it closes search,
  // mirroring the Esc key's collapse.
  const handleClear = useCallback(
    function handleClear() {
      if (query.length === 0) {
        onRequestClose?.();
        return;
      }
      setQuery('');
      resetSearch();
      searchInputRef.current?.focus();
    },
    [onRequestClose, query.length, resetSearch],
  );

  const totalPages = Math.max(1, Math.ceil(totalResults / SEARCH_RESULTS_PER_PAGE));
  // A search has landed (or is in flight) exactly when a submitted query exists.
  const hasSearched = submittedQuery.length > 0;
  const showPagination = hasSearched && !isLoading && totalResults > 0 && totalPages > 1;

  const handlePreviousPage = useCallback(
    function handlePreviousPage() {
      if (isLoading || currentPage <= 1 || !submittedQuery) return;
      void runSearch(submittedQuery, currentPage - 1);
    },
    [currentPage, isLoading, runSearch, submittedQuery],
  );

  const handleNextPage = useCallback(
    function handleNextPage() {
      if (isLoading || currentPage >= totalPages || !submittedQuery) return;
      void runSearch(submittedQuery, currentPage + 1);
    },
    [currentPage, isLoading, runSearch, submittedQuery, totalPages],
  );

  const paginationButtonClassName = cn(
    'flex h-8 cursor-pointer items-center justify-center rounded-full border-[0.5px] px-3 font-sans text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50',
    onImage
      ? 'border-white/20 text-white hover:bg-white/10'
      : 'border-gray-300 text-gray-600 hover:bg-white dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800',
  );
  const mutedTextClassName = onImage ? 'text-white/60' : 'text-gray-500 dark:text-neutral-400';

  let resultsView = null;

  if (isLoading) {
    resultsView = <div className={cn('flex items-center justify-center py-8 text-cursed', mutedTextClassName)}>Searching…</div>;
  } else if (results.length === 0) {
    resultsView = <div className={cn('flex items-center justify-center py-8 text-cursed', mutedTextClassName)}>No results</div>;
  } else {
    resultsView = (
      <div className="flex flex-col py-2">
        {results.map((message, index) => (
          <React.Fragment key={message.id}>
            {index > 0 && <div className={RESULT_DIVIDER_CLASS[onImage ? 'onImage' : 'plain']} />}
            <SearchResultRow
              message={message}
              author={authorsById?.[message.author_id] || null}
              routeEntitySnapshots={routeEntitySnapshots}
              onOpen={handleOpenSearchResult}
              onImage={onImage}
              highlightTerm={submittedQuery}
            />
          </React.Fragment>
        ))}
      </div>
    );
  }

  return (
    <div ref={rootRef} className="flex w-full min-w-0 items-center justify-self-start" data-app-region="none">
      {/* Same pill chrome as the nav trio it temporarily replaces. pl-[9px] keeps the
          magnifier exactly where the trio's Search button icon sat (2px pill padding +
          7px into the size-8 button), so the glyph holds still while the pill grows. */}
      <form
        onSubmit={handleSubmit}
        onClick={() => searchInputRef.current?.focus({ preventScroll: true })}
        className={cn(
          'flex h-9 w-full cursor-text items-center gap-2 overflow-hidden rounded-full pr-2.5 pl-[9px]',
          'min-w-0 transition-[max-width] duration-250 ease-fluid motion-reduce:transition-none',
          isPillExpanded ? 'max-w-72' : 'max-w-9',
          onImage
            ? 'bg-image-glass-card'
            : 'border sm:border-[0.5px] border-white bg-gray-50 shadow-[0_4px_16px_0_rgba(0,0,0,0.1)] dark:border-white/10 dark:bg-neutral-900',
        )}
      >
        <Search
          className={cn('size-4.5 shrink-0', onImage ? 'text-white' : 'text-gray-500 dark:text-neutral-400')}
          strokeWidth={2}
          absoluteStrokeWidth
        />
        <input
          ref={searchInputRef}
          type="search"
          value={query}
          onChange={handleQueryChange}
          placeholder="Search"
          maxLength={MAX_QUERY_LENGTH}
          data-1p-ignore="true"
          className={cn(
            'min-w-0 flex-1 bg-transparent text-[15px] leading-5 outline-none [&::-webkit-search-cancel-button]:hidden',
            onImage
              ? 'text-white placeholder:text-white/60'
              : 'text-black placeholder:text-gray-400 dark:text-neutral-100 dark:placeholder:text-neutral-500',
          )}
        />
        <button
          type="button"
          data-app-region="none"
          onClick={handleClear}
          aria-label={query.length > 0 ? 'Clear search' : 'Close search'}
          className={cn(
            'flex shrink-0 cursor-pointer items-center justify-center focus:outline-none',
            onImage ? 'text-white' : 'text-black dark:text-neutral-100',
          )}
        >
          <X className="size-4" strokeWidth={1.5} absoluteStrokeWidth />
        </button>
      </form>

      {hasSearched && (
        // Anchored below the nav bar like the Channels card, chat stays visible behind.
        <div
          data-app-region="none"
          className={cn(
            'absolute top-[calc(100%+8px)] left-4 z-(--stack-thread-list) flex max-h-[min(70vh,40rem)] w-[min(37.5rem,calc(100%-32px))] flex-col overflow-hidden rounded-2xl',
            onImage
              ? 'bg-image-glass-card'
              : 'border-[0.5px] border-white bg-gray-50 shadow-[0_4px_16px_0_rgba(0,0,0,0.1)] backdrop-blur-md dark:border-white/10 dark:bg-neutral-900',
          )}
        >
          <div ref={resultsScrollRef} className="min-h-0 flex-1 overflow-y-auto">
            {resultsView}
          </div>
          {showPagination ? (
            <nav
              aria-label="Search results pagination"
              className={cn(
                'flex shrink-0 flex-wrap items-center justify-between gap-3 border-t-[0.5px] px-4 py-3',
                onImage ? 'border-white/20' : 'border-gray-200 dark:border-neutral-800',
              )}
            >
              <span className={cn('font-sans text-xs', mutedTextClassName)}>{totalResults.toLocaleString()} results</span>
              <div className="flex items-center gap-2">
                <button type="button" onClick={handlePreviousPage} disabled={currentPage <= 1} className={paginationButtonClassName}>
                  Previous
                </button>
                <span className={cn('min-w-20 text-center font-sans text-xs', mutedTextClassName)}>
                  Page {currentPage} of {totalPages}
                </span>
                <button type="button" onClick={handleNextPage} disabled={currentPage >= totalPages} className={paginationButtonClassName}>
                  Next
                </button>
              </div>
            </nav>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default HeaderSearch;
